/**
 * AgentFlowHistoryButton Component
 *
 * A standalone button that opens the AgentFlowHistory modal.
 * Can be placed anywhere in the UI to view workflow execution history.
 */

import { ListTree } from 'lucide-react';
import React, { useState } from 'react';

import { AgentFlowHistory } from './AgentFlowHistory';

interface AgentFlowHistoryButtonProps {
  /** Matter ID for API calls */
  matterId: string;
  /** Coverage ID for API calls */
  coverageId: string;
  /** Task ID for workflow lookup */
  taskId: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Custom button text (default: "View Workflow History") */
  label?: string;
  /** Custom className for the button */
  className?: string;
  /** Flow ID to show correct step options for jump (default: non_disclosure_3p) */
  flowId?: 'non_disclosure_3p' | 'progressive_1p' | 'disclosure';
}

export const AgentFlowHistoryButton: React.FC<AgentFlowHistoryButtonProps> = ({
  matterId,
  coverageId,
  taskId,
  variant = 'secondary',
  size = 'md',
  label = 'View Workflow History',
  className,
  flowId,
}) => {
  const [open, setOpen] = useState(false);

  const variantClasses = {
    primary: 'bg-violet-600 text-white hover:bg-violet-700',
    secondary: 'border border-zinc-300 text-zinc-700 hover:bg-zinc-50',
    ghost: 'text-zinc-700 hover:bg-zinc-100',
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-xs',
    lg: 'px-4 py-2.5 text-sm',
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 font-semibold rounded-md transition-colors ${variantClasses[variant]} ${sizeClasses[size]} ${className || ''}`}
      >
        <ListTree className="h-4 w-4" />
        {label}
      </button>

      <AgentFlowHistory
        matterId={matterId}
        coverageId={coverageId}
        taskId={taskId}
        open={open}
        onOpenChange={setOpen}
        flowId={flowId}
      />
    </>
  );
};

export default AgentFlowHistoryButton;
